// the enhanced select.
import { latinDigits } from '../rules.ts';
import { $, bankMark, closeLayer, esc, fa, icon, narrow, openLayer } from './core.ts';

/** One item of a remote list (the server or cfg.remote answers these). */
export interface RemoteItem {
  value: string | number;
  label: string;
  description?: string;
  initials?: string;
}
/** How a page configures an enhanced select (all optional). */
export interface SelectConfig {
  remote?: (q: string) => Promise<RemoteItem[]>;
  minChars?: number;
  placeholder?: string;
  clearable?: boolean;
}
/** What `select._ds` offers page scripts (the select2 bridge uses it). */
export interface SelectHandle {
  open(): void;
  close(): void;
  refresh(): void;
  configure(c: SelectConfig): void;
}
/** A select this script enhanced. */
export type EnhancedSelect = HTMLSelectElement & { _ds?: SelectHandle };

/** An option as the list shows it: value, label, description, initials, bank, icon, group, disabled and why. */
interface Opt {
  v: string;
  l: string;
  d?: string;
  av?: string;
  bank?: string;
  ico?: string;
  g?: string | null;
  dis?: boolean;
  why?: string;
  el?: HTMLOptionElement;
}

// ---------------- select ----------------
export const normQ = (s: string): string =>
  latinDigits(s).replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/‌/g, ' ').toLowerCase();
function hl(label: string, q: string): string {
  if (!q) return esc(label);
  const i = normQ(label).indexOf(normQ(q));
  return i < 0
    ? esc(label)
    : esc(label.slice(0, i)) +
        '<mark>' +
        esc(label.slice(i, i + q.length)) +
        '</mark>' +
        esc(label.slice(i + q.length));
}
let selSeq = 0;
/** T-171: the fields a server lookup reads — data-depends="key=controlId,…" (FormView.Depends). */
export function dependencies(select: HTMLSelectElement): { key: string; el: HTMLInputElement | HTMLSelectElement }[] {
  return (select.dataset.depends || '')
    .split(',')
    .filter(Boolean)
    .map(pair => {
      const [key, id] = pair.split('=');
      return { key, el: document.getElementById(id || key) as HTMLInputElement | HTMLSelectElement | null };
    })
    .filter((d): d is { key: string; el: HTMLInputElement | HTMLSelectElement } => d.el !== null);
}
/** The dependency values as query parameters («&personType=1&specializations=»). */
export function dependencyQuery(select: HTMLSelectElement): string {
  return dependencies(select)
    .map(d => '&' + encodeURIComponent(d.key) + '=' + encodeURIComponent(d.el.value))
    .join('');
}
// cfg (all optional): { remote: q => Promise<[{value,label,description}]>, minChars, placeholder, clearable }.
// Called again on an enhanced select it only updates the configuration (the select2 bridge does that).
export function enhanceSelect(select: EnhancedSelect, cfg?: SelectConfig): void {
  if (select.dataset.dsReady) {
    if (cfg) select._ds?.configure(cfg);
    return;
  }
  select.dataset.dsReady = '1';
  const id = 'dss' + ++selSeq,
    multiple = select.multiple;
  let mode = select.dataset.search || (select.options.length > 8 ? 'local' : 'none'),
    remote: SelectConfig['remote'] | null = null,
    minChars = select.dataset.minChars ? Number(select.dataset.minChars) : 2;
  const host = document.createElement('div');
  host.className = 'ds-select';
  select.parentNode!.insertBefore(host, select);
  host.appendChild(select);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
  const label = select.labels?.[0];
  host.insertAdjacentHTML(
    'afterbegin',
    `<button type="button" class="ds-select-btn" id="${id}-btn" aria-haspopup="listbox" aria-expanded="false"${select.disabled ? ' aria-disabled="true"' : ''}><span class="ds-select-val"></span>${icon('chev-down', 'caret')}</button>`,
  );
  const btn = host.querySelector<HTMLButtonElement>('.ds-select-btn')!,
    val = host.querySelector('.ds-select-val')!;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
  if (label) {
    label.htmlFor = btn.id;
  }
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  // eslint-disable-next-line prefer-const -- one declaration list where the other names are reassigned
  let known = new Map<string, Opt>(),
    opts: Opt[] = [],
    shown: Opt[] = [],
    q = '',
    act = -1,
    pop: HTMLDivElement | null = null,
    scrim: HTMLDivElement | null = null,
    loading = false,
    req = 0;
  const readOpts = (): void => {
    opts = [...select.options]
      .filter(o => o.value !== '' || !o.disabled)
      .map(o => ({
        v: o.value,
        l: o.textContent.trim(),
        d: o.dataset.desc,
        av: o.dataset.av,
        bank: o.dataset.bank,
        ico: o.dataset.icon,
        g: o.parentElement!.tagName === 'OPTGROUP' ? (o.parentElement as HTMLOptGroupElement).label : null,
        dis: o.disabled,
        why: o.dataset.why,
        el: o,
      }))
      .filter(o => o.v !== '');
    opts.forEach(o => known.set(o.v, o));
  };
  readOpts();
  let placeholder =
    select.dataset.placeholder || [...select.options].find(o => o.value === '')?.textContent || 'انتخاب کنید';
  const lead = (o: Opt): string =>
    o.av
      ? `<span class="ds-av ds-av-sm">${esc(o.av)}</span>`
      : o.bank
        ? `<span class="ds-bank ds-bank-sm"><img src="${esc(bankMark(o.bank))}" alt=""></span>`
        : o.ico
          ? icon(o.ico)
          : '';
  const selected = (): string[] => [...select.options].filter(o => o.selected && o.value !== '').map(o => o.value);
  function drawValue(): void {
    const vs = selected();
    if (select.disabled) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
    if (!vs.length) val.innerHTML = `<span class="ph">${esc(placeholder)}</span>`;
    else if (!multiple) {
      const o = known.get(vs[0]);
      val.innerHTML = `<span class="one">${o ? lead(o) + esc(o.l) : esc(vs[0])}</span>`;
    } else
      val.innerHTML =
        vs
          .slice(0, 3)
          .map(v => {
            const o = known.get(v);
            return `<span class="ds-tag">${esc(o?.l ?? v)}<span role="button" tabindex="-1" data-rm="${esc(v)}" aria-label="حذف ${esc(o?.l ?? v)}">${icon('close')}</span></span>`;
          })
          .join('') + (vs.length > 3 ? `<span class="more">+${fa(vs.length - 3)}</span>` : '');
    host.querySelector('.ds-select-clear')?.remove();
    if (select.dataset.clearable !== undefined && vs.length && !select.required)
      btn.insertBefore(
        Object.assign(document.createElement('span'), {
          className: 'ds-select-clear',
          innerHTML: icon('close'),
          role: 'button',
          ariaLabel: 'پاک کردن',
        }),
        btn.lastChild,
      );
  }
  // T-171 (design D16): values a page leaves out (data-exclude = a JSON array; a rows field puts its unique column there)
  const excluded = (): Set<string> => {
    try {
      return new Set(JSON.parse(select.dataset.exclude || '[]') as string[]);
    } catch {
      return new Set<string>();
    }
  };
  const visible = (): Opt[] => {
    const out = excluded();
    return (
      mode === 'server' ? opts : opts.filter(o => !q || normQ(o.l + ' ' + (o.d || '')).includes(normQ(q)))
    ).filter(o => !out.has(o.v));
  };
  function drawList(): void {
    if (!pop) return;
    const list = pop.querySelector('.ds-select-list')!;
    shown = visible();
    if (mode === 'server' && normQ(q).length < minChars) {
      list.innerHTML = `<div class="ds-select-empty">${icon('search')}حداقل ${fa(minChars)} حرف بنویسید</div>`;
      return;
    }
    if (loading) {
      list.innerHTML =
        '<div class="ds-select-empty"><span class="ds-spin" aria-hidden="true"></span>در حال جست‌وجو…</div>';
      return;
    }
    if (!shown.length) {
      list.innerHTML = `<div class="ds-select-empty">${icon('search')}موردی پیدا نشد${q ? ' برای «' + esc(q) + '»' : ''}</div>`;
      return;
    }
    const sel = new Set(selected());
    let h = '',
      g: string | null = null;
    shown.forEach((o, i) => {
      if (o.g && o.g !== g) {
        g = o.g;
        h += `<div class="ds-select-grp" role="presentation">${esc(g)}</div>`;
      }
      h +=
        `<div class="ds-opt${i === act ? ' act' : ''}" role="option" id="${id}-o${i}" data-i="${i}" aria-selected="${sel.has(o.v)}"${o.dis ? ' aria-disabled="true"' : ''}>` +
        (multiple ? '<span class="box" aria-hidden="true"></span>' : '') +
        lead(o) +
        `<span class="txt"><span>${hl(o.l, q)}</span>${o.d || o.why ? `<small>${esc(o.dis && o.why ? o.why : o.d)}</small>` : ''}</span>` +
        (multiple ? '' : icon('check', 'tick')) +
        '</div>';
    });
    list.innerHTML = h;
    list.querySelector('.act')?.scrollIntoView({ block: 'nearest' });
    const inp = pop.querySelector('input');
    if (inp) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- kept as written (T-081 moved it verbatim)
      act >= 0
        ? inp.setAttribute('aria-activedescendant', `${id}-o${act}`)
        : inp.removeAttribute('aria-activedescendant');
    }
    const foot = pop.querySelector('.ds-select-foot span');
    if (foot) foot.textContent = sel.size ? fa(sel.size) + ' مورد انتخاب شد' : 'چیزی انتخاب نشده';
  }
  function place(): void {
    if (!pop || narrow(640)) return;
    const r = btn.getBoundingClientRect(),
      top = $('.ds-topbar')?.getBoundingClientRect().bottom ?? 0;
    if (r.bottom < top || r.top > innerHeight) {
      close(false);
      return;
    }
    const w = Math.max(r.width, 240),
      h = Math.min(pop.scrollHeight, 380),
      below = innerHeight - r.bottom - 8;
    pop.style.width = w + 'px';
    pop.style.left = Math.max(8, Math.min(r.right - w, innerWidth - w - 8)) + 'px';
    if (below < h && r.top > below) {
      pop.style.top = '';
      pop.style.bottom = innerHeight - r.top + 6 + 'px';
    } else {
      pop.style.bottom = '';
      pop.style.top = r.bottom + 6 + 'px';
    }
  }
  function choose(o: Opt | undefined): void {
    if (!o || o.dis) return;
    if (mode === 'server' && !select.querySelector(`option[value="${CSS.escape(o.v)}"]`))
      select.add(new Option(o.l, o.v));
    const opt = select.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(o.v)}"]`)!;
    if (multiple) opt.selected = !opt.selected;
    else {
      [...select.options].forEach(x => {
        x.selected = x === opt;
      });
    }
    select.dispatchEvent(new Event('change', { bubbles: true }));
    drawValue();
    if (multiple) drawList();
    else close();
  }
  const onOutside = (e: Event): void => {
    if (pop && !pop.contains(e.target as Node) && !host.contains(e.target as Node)) close(false);
  };
  const onScroll = (e: Event): void => {
    if (pop && !pop.contains(e.target as Node)) place();
  };
  function open(): void {
    if (pop || select.disabled) return;
    openLayer(closeQuiet);
    q = '';
    act = -1;
    host.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
    scrim = Object.assign(document.createElement('div'), { className: 'ds-select-scrim' });
    scrim.addEventListener('click', () => close());
    pop = document.createElement('div');
    pop.className = 'ds-select-pop';
    pop.dir = 'rtl';
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
    const title = label?.textContent.replace('*', '').trim() || placeholder;
    pop.innerHTML =
      `<div class="ds-select-sheet-h">${esc(title)}</div>` +
      (mode !== 'none'
        ? `<div class="ds-select-search">${icon('search')}<input role="combobox" aria-expanded="true" aria-controls="${id}-l" aria-autocomplete="list" autocomplete="off" placeholder="${mode === 'server' && !remote ? 'نام، کد ملی یا موبایل' : 'جست‌وجو'}" aria-label="جست‌وجو"></div>`
        : '') +
      (multiple && select.dataset.selectAll !== undefined
        ? '<div class="ds-select-tools"><span></span><button type="button" class="ds-link" data-all>انتخاب همه</button></div>'
        : '') +
      `<div class="ds-select-list" role="listbox" id="${id}-l"${multiple ? ' aria-multiselectable="true"' : ''} aria-label="${esc(title)}" tabindex="-1"></div>` +
      (multiple
        ? '<div class="ds-select-foot"><span></span><button type="button" class="btn btn-primary btn-sm" data-done>تأیید</button></div>'
        : '');
    document.body.append(scrim, pop);
    drawList();
    place();
    const inp = pop.querySelector('input');
    (inp || pop.querySelector<HTMLElement>('.ds-select-list')!).focus({ preventScroll: true });
    if (!inp) {
      act = Math.max(
        0,
        shown.findIndex(o => selected().includes(o.v)),
      );
      drawList();
    }
    pop.addEventListener('keydown', onKey);
    pop.addEventListener('mousedown', e => {
      if ((e.target as Element).closest('.ds-opt')) e.preventDefault();
    });
    pop.addEventListener('click', e => {
      const o = (e.target as Element).closest<HTMLElement>('.ds-opt');
      if (o) {
        act = +o.dataset.i!;
        choose(shown[act]);
        return;
      }
      if ((e.target as Element).closest('[data-done]')) {
        close();
        return;
      }
      if ((e.target as Element).closest('[data-all]')) {
        const en = shown.filter(x => !x.dis),
          all = en.every(x => selected().includes(x.v));
        en.forEach(x => {
          const op = select.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(x.v)}"]`);
          if (op) op.selected = !all;
        });
        select.dispatchEvent(new Event('change', { bubbles: true }));
        drawValue();
        drawList();
      }
    });
    inp?.addEventListener('input', () => {
      q = inp.value;
      act = 0;
      if (mode === 'server') search();
      drawList();
    });
    document.addEventListener('pointerdown', onOutside, true);
    addEventListener('scroll', onScroll, true);
    addEventListener('resize', place);
    if (mode === 'server' && minChars === 0) {
      search();
      drawList();
    } // a remote list that shows its first page on open
  }
  function search(): void {
    const my = ++req;
    if (normQ(q).length < minChars) {
      opts = [];
      loading = false;
      return;
    }
    loading = true;
    setTimeout(
      async () => {
        if (my !== req) return;
        try {
          const items = (
            remote
              ? await remote(q)
              : await (
                  await fetch(
                    select.dataset.source! +
                      (select.dataset.source!.includes('?') ? '&' : '?') +
                      'q=' +
                      encodeURIComponent(q) +
                      dependencyQuery(select),
                    { credentials: 'same-origin', headers: { Accept: 'application/json' } },
                  )
                ).json()
          ) as RemoteItem[];
          if (my !== req) return;
          opts = items.map(x => ({ v: String(x.value), l: x.label, d: x.description, av: x.initials }));
          opts.forEach(o => known.set(o.v, o));
        } catch {
          opts = [];
        }
        loading = false;
        act = 0;
        drawList();
        place();
      },
      remote && !q ? 0 : 300,
    );
  }
  function close(refocus = true): void {
    if (!pop) return;
    host.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
    pop.remove();
    scrim!.remove();
    pop = null;
    closeLayer(closeQuiet);
    document.removeEventListener('pointerdown', onOutside, true);
    removeEventListener('scroll', onScroll, true);
    removeEventListener('resize', place);
    if (refocus) btn.focus({ preventScroll: true });
  }
  function closeQuiet(): void {
    close(false);
  }
  let typed = '',
    typedAt = 0;
  function onKey(e: KeyboardEvent): void {
    const n = shown.length;
    if (e.key === 'ArrowDown') {
      act = (act + 1) % Math.max(n, 1);
      drawList();
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      act = (act - 1 + n) % Math.max(n, 1);
      drawList();
      e.preventDefault();
    } else if (e.key === 'Home') {
      act = 0;
      drawList();
      e.preventDefault();
    } else if (e.key === 'End') {
      act = n - 1;
      drawList();
      e.preventDefault();
    } else if (e.key === 'Enter' || (e.key === ' ' && mode === 'none')) {
      if (act >= 0) choose(shown[act]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      close();
      e.stopPropagation();
    } else if (e.key === 'Tab') close(false);
    else if (mode === 'none' && e.key.length === 1) {
      const now = Date.now();
      typed = (now - typedAt > 700 ? '' : typed) + e.key;
      typedAt = now;
      const i = shown.findIndex(o => normQ(o.l).startsWith(normQ(typed)));
      if (i >= 0) {
        act = i;
        drawList();
      }
    }
  }
  btn.addEventListener('click', e => {
    const rm = (e.target as Element).closest<HTMLElement>('[data-rm]');
    if (rm) {
      e.stopPropagation();
      const op = select.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(rm.dataset.rm!)}"]`);
      if (op) op.selected = false;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      drawValue();
      return;
    }
    if ((e.target as Element).closest('.ds-select-clear')) {
      e.stopPropagation();
      [...select.options].forEach(o => {
        o.selected = o.value === '';
      });
      select.dispatchEvent(new Event('change', { bubbles: true }));
      drawValue();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- kept as written (T-081 moved it verbatim)
    pop ? close() : open();
  });
  btn.addEventListener('keydown', e => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !pop) {
      open();
      e.preventDefault();
    }
  });
  select.addEventListener('ds:refresh', () => {
    if (mode !== 'server') readOpts();
    else
      [...select.options].forEach(o => {
        if (o.value && !known.has(o.value)) known.set(o.value, { v: o.value, l: o.textContent.trim() });
      });
    drawValue();
  });
  new MutationObserver(() => {
    if (mode !== 'server') readOpts();
    else select.dispatchEvent(new Event('ds:refresh'));
    drawValue();
  }).observe(select, { childList: true, subtree: true });
  // T-171: a lookup that reads other fields (data-depends="key=id,…") starts again when one of them changes
  dependencies(select).forEach(({ el }) =>
    el.addEventListener('change', () => {
      if (!selected().length) return;
      [...select.options].forEach(o => {
        if (o.value !== '') o.remove();
      });
      select.value = '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      drawValue();
    }),
  );
  select._ds = {
    open,
    close: () => close(false),
    refresh: () => select.dispatchEvent(new Event('ds:refresh')),
    configure(c) {
      if (c.remote) {
        remote = c.remote;
        mode = 'server';
        minChars = c.minChars ?? 0;
      }
      if (c.placeholder) placeholder = c.placeholder;
      if (c.clearable) select.dataset.clearable = '';
      drawValue();
    },
  };
  if (cfg) select._ds.configure(cfg);
  drawValue();
}
